import { Trans } from "@lingui/react/macro"
import { Center, Loader, Stack, Text } from "@mantine/core"
import { LiteRecordingFocus } from "../../RecordingSurface/LiteRecordingFocus"
import { PreRecord } from "../../RecordingSurface/PreRecord"
import { RecordingFocus } from "../../RecordingSurface/RecordingFocus"
import { Topbar } from "../../RecordingSurface/Topbar"
import { FinishConfirmModal } from "../FinishConfirmModal"
import { LegacyRecordingPanel } from "../LegacyRecordingPanel"
import { RecoveryBanner } from "../RecoveryBanner"
import type { IViewProps } from "./interfaces"

/**
 * Phase router — picks the right composition based on `phase`. Pure: every
 * branch receives its complete prop tree from the smart wrapper. Stories
 * pass any phase + matching `*Args` payload to exercise that surface.
 */
function noop(): void {}

export const RecordingControlsView = ({
  phase,
  prerecordArgs,
  focusArgs,
  recoveryArgs,
  legacyArgs,
  isLite = false,
  autoStart = false,
  provisionalOffline = false,
}: IViewProps): React.JSX.Element | null => {
  if (phase === "prerecord") {
    if (!prerecordArgs) return null
    const permission = prerecordArgs.permission
    const waitingOnMic =
      (isLite || autoStart) &&
      !provisionalOffline &&
      (permission === "granted" || permission === "unknown") &&
      prerecordArgs.error === null
    if (waitingOnMic) {
      return (
        <div data-testid="lite-mic-warming">
          <Topbar
            phase="prerecord"
            subject={prerecordArgs.subject}
            startTime={prerecordArgs.startTime}
            onRename={prerecordArgs.onRename}
            exitLabel={<Trans>Meetings</Trans>}
          />
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader color="brand" />
              <Text c="dimmed">
                <Trans>Getting the microphone ready…</Trans>
              </Text>
            </Stack>
          </Center>
        </div>
      )
    }
    return (
      <PreRecord
        subject={prerecordArgs.subject}
        startTime={prerecordArgs.startTime}
        permission={prerecordArgs.permission}
        onRecheckPermission={prerecordArgs.onRecheckPermission}
        onAllowMicrophone={prerecordArgs.onAllowMicrophone ?? noop}
        onStart={prerecordArgs.onStart}
        onRename={prerecordArgs.onRename}
        languages={prerecordArgs.languages}
        autoDetect={prerecordArgs.autoDetect}
        mode={prerecordArgs.mode ?? "full"}
        transcription={prerecordArgs.transcription ?? "live"}
        modeLabel={prerecordArgs.modeLabel}
        helpEnvironment={prerecordArgs.helpEnvironment}
        audio={prerecordArgs.audio}
        error={prerecordArgs.error}
        onDismissError={prerecordArgs.onDismissError}
      />
    )
  }

  if (phase === "lite-focus") {
    if (!focusArgs) return null
    return (
      <>
        <LiteRecordingFocus {...focusArgs} />
        <FinishConfirmModal
          opened={focusArgs.finishConfirmOpen}
          meetingLabel={focusArgs.meetingLabel}
          onClose={focusArgs.onCancelFinish}
          onConfirm={focusArgs.onConfirmFinish}
          loading={focusArgs.finishing}
        />
      </>
    )
  }

  if (phase === "focus") {
    if (!focusArgs) return null
    return (
      <>
        <RecordingFocus
          paused={focusArgs.paused}
          finalizing={focusArgs.finalizing}
          offlineRecordingOnly={focusArgs.offlineRecordingOnly}
          error={focusArgs.error}
          onDismissError={focusArgs.onDismissError}
          wsReconnecting={focusArgs.wsReconnecting}
          health={focusArgs.health}
          healthIssues={focusArgs.healthIssues}
          processingStage={focusArgs.processingStage}
          subject={focusArgs.subject}
          startTime={focusArgs.startTime}
          elapsed={focusArgs.elapsed}
          level={focusArgs.level}
          audio={focusArgs.audio}
          segments={focusArgs.segments}
          interimText={focusArgs.interimText}
          interimSpeaker={focusArgs.interimSpeaker}
          transcriptStaleSec={focusArgs.transcriptStaleSec}
          progressiveSummary={focusArgs.progressiveSummary}
          progressiveSummaryCount={focusArgs.progressiveSummaryCount}
          progressiveSummaryAt={focusArgs.progressiveSummaryAt}
          onPause={focusArgs.onPause}
          onResume={focusArgs.onResume}
          onFinish={focusArgs.onRequestFinish}
          meetingId={focusArgs.meetingId}
          onRename={focusArgs.onRename}
          coachEnabled={focusArgs.coachEnabled}
          coachTone={focusArgs.coachTone}
          coachSuggestion={focusArgs.coachSuggestion}
          coachHistory={focusArgs.coachHistory}
          coachAsking={focusArgs.coachAsking}
          onCoachToggle={focusArgs.onCoachToggle}
          onCoachToneChange={focusArgs.onCoachToneChange}
          onCoachAsk={focusArgs.onCoachAsk}
          speakerHypotheses={focusArgs.speakerHypotheses}
          hypothesisPending={focusArgs.hypothesisPending}
          onHypothesisAction={focusArgs.onHypothesisAction}
          languageCode={focusArgs.languageCode}
          languageSignal={focusArgs.languageSignal}
          languageIsWorkspaceDefault={focusArgs.languageIsWorkspaceDefault}
          languageSuggestion={focusArgs.languageSuggestion}
          onAcceptLanguageSwitch={focusArgs.onAcceptLanguageSwitch}
          onKeepCurrentLanguage={focusArgs.onKeepCurrentLanguage}
        />
        <FinishConfirmModal
          opened={focusArgs.finishConfirmOpen}
          meetingLabel={focusArgs.meetingLabel}
          onClose={focusArgs.onCancelFinish}
          onConfirm={focusArgs.onConfirmFinish}
          loading={focusArgs.finishing}
        />
      </>
    )
  }

  if (phase === "recovery") {
    if (!recoveryArgs) return null
    return <RecoveryBanner {...recoveryArgs} />
  }

  if (phase === "legacy") {
    if (!legacyArgs) return null
    return <LegacyRecordingPanel {...legacyArgs} />
  }

  return null
}
